import React from 'react';

/**
 * Mascot-led banner that introduces the comments section. Mirrors Figma
 * 9146:110131 — squirrel mascot on the left, two-line purple headline
 * centred to the right with a soft "Tery" curl behind it.
 *
 * Decorative — `aria-hidden` on the imagery.
 */
export default function CommentsHeroBanner({
  targetType = 'course',
}: {
  /** What is being discussed — the headline names it (Aleix, 2026-09-15:
   *  a program's banner was inviting thoughts about "this course"). */
  targetType?: 'course' | 'program';
}) {
  return (
    <div className="relative flex items-center gap-4 sm:gap-8 px-4 sm:px-6 py-4">
      <img
        src="/tery/tery_techy.png"
        alt=""
        aria-hidden="true"
        className="relative z-10 w-[88px] sm:w-[112px] h-auto shrink-0 select-none pointer-events-none"
        draggable={false}
      />
      <div className="relative flex-1 flex items-center justify-center min-h-[124px] py-2">
        <TeryCurl className="absolute inset-x-0 top-1/2 -translate-y-1/2 w-[112%] max-w-none sm:w-full sm:max-w-[560px] mx-auto opacity-60 pointer-events-none" />
        <h2 className="relative z-10 text-base sm:text-xl font-extrabold text-primary-500 text-center capitalize leading-snug tracking-tight">
          See What Others Think About This {targetType === 'program' ? 'Program' : 'Course'}
          <br />
          And Share Your Thoughts Too!
        </h2>
      </div>
    </div>
  );
}

/** Decorative cursive "Tery" curl behind the headline. */
function TeryCurl({ className }: { className?: string }) {
  return (
    <svg
      /* Padded canvas: the stroke is 22 wide with round caps, and the curve's
         crests sat within a few units of the old 0 0 360 96 box, so the arcs
         were sliced by the SVG's own edge — no CSS involved (Aleix,
         2026-09-15, "the curved line is a bit cropped"). */
      viewBox="-20 -24 400 144"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <path
        d="M20 60 C 60 10, 110 10, 130 60 S 200 110, 230 50 S 320 0, 345 60"
        stroke="rgb(var(--color-primary) / 0.18)"
        strokeWidth="22"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}
